import React, { useState, useEffect } from "react";
import {
  Pencil,
  Plus,
  User,
  Edit,
  Search,
  RefreshCw,
  X,
  Briefcase,
  CheckCircle,
  XCircle,
  AlertCircle,  
  Download,
} from "lucide-react";
import DownloadModal from "../Components/Download/DownloadModal";

import apiService from "../config";
import { toast } from "react-toastify"; // Keeping toast for general notifications

import { useLanguage } from "../StoreContext/LanguageContext";

const RoleManagement = () => {
  // Define roles that do not require project or proposal assignment
  const rolesWithoutProposal = [
    "Samanvay Officer",
    "District Collector",
    "Project Incharge",
    "Deputy Collector",
  ];
  const { language, translations } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    usr_id: null,
    mobileNo: "",
    role: "", // Stores role name for display
    user_role_id: null, // Stores role ID for API
    projectName: "", // Stores project name for display
    project_id: null, // Stores project ID for API
    proposalName: "", // Stores proposal name for display
    acquisition_proposal_id: null, // Stores proposal ID for API
    status: false, // Default to inactive for new users
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [usernameSearchTerm, setUsernameSearchTerm] = useState("");

  // New state for download modal
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Form validation state
  const [formErrors, setFormErrors] = useState({
    username: "",
    mobileNo: "",
    role: "",
    projectName: "",
    proposalName: "",
  });

  // Touch state to track if fields have been modified
  const [touchedFields, setTouchedFields] = useState({});

  // Loading state for form submission to prevent multiple clicks
  const [submitLoading, setSubmitLoading] = useState(false);

  // General alert message state for success/error
  const [alertMessage, setAlertMessage] = useState({ type: "", message: "" });

  // Fetch initial data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch all necessary data (users, roles, projects, proposals)
  const fetchAllData = async () => {
    setLoading(true);
    setAlertMessage({ type: "", message: "" }); // Clear previous alerts
    try {
      await Promise.all([
        fetchUsers(),
        fetchRoles(),
        fetchProjects(),
        // fetchProposals is called conditionally based on project selection
      ]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to load data. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch users with their assigned roles
  const fetchUsers = async () => {
    try {
      const response = await apiService.getAllAssignedRoles();
      if (response && response.data) {
        const formattedUsers = response.data.map((user, index) => ({
          id: `${user.usr_id}-${user.user_role_id}-${user.project_id || "no-proj"}-${user.acquisition_proposal_id || "no-prop"}-${index}`, // Unique ID for React key
          usr_id: user.usr_id,
          username: user.full_name || "",
          mobileNo: user.mobile ? `+91 ${user.mobile}` : "",
          role: user.user_role_name,
          user_role_id: user.user_role_id,
          designation: user.designation,
          project_id: user.project_id,
          projectName: user.project_name,
          proposalName: user.acquisition_proposal_no,
          acquisition_proposal_id: user.acquisition_proposal_id,
          status: user.is_active === 1,
        }));
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to load users. Please try again.",
      });
    }
  };

  // Fetch available user roles
  const fetchRoles = async () => {
    try {
      const response = await apiService.getAllUserRoles();
      if (response && response.data) {
        const rolesList = response.data.map((role) => ({
          id: role.user_role_id,
          name: role.usr_role,
          designation: role.designation
        }));
        setRoles(rolesList);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to load roles. Please try again.",
      });
    }
  };

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      const response = await apiService.getAllProjectsafterhomepage();
      if (response && response.data) {
        const projectsList = response.data.map((project) => ({
          id: project.project_id,
          name: project.project_name,
        }));
        setProjects(projectsList);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to load projects. Please try again.",
      });
    }
  };

  // Fetch proposals based on a selected project ID
  const fetchProposals = async (projectId) => {
    if (!projectId) {
      setProposals([]); // Clear proposals if no project is selected
      return;
    }
    try {
      const response = await apiService.getProposalsByProjectId(projectId);
      if (response && response.data) {
        const proposalList = response.data.map((proposal) => ({
          id: proposal.acquisition_proposal_id,
          name: proposal.acquisition_proposal_no,
        }));
        setProposals(proposalList);
      }
    } catch (error) {
      console.error("Error fetching proposals:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to load proposals. Please try again.",
      });
    }
  };

  // Search users by username for the form dropdown
  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiService.searchUser(searchTerm);
      if (response && response.data) {
        const formattedResults = response.data.map((user) => ({
          id: user.usr_id,
          username: user.full_name,
          mobileNo: user.mobile ? `+91 ${user.mobile}` : "",
          email: user.email,
        }));
        setSearchResults(formattedResults);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error("Error searching users:", error);
      toast.error("Error searching for users. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  // Filter and sort users displayed in the table
  useEffect(() => {
    if (!users || !Array.isArray(users)) return;

    let result = [...users];

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter((user) => {
        const username = user.username ? user.username.toLowerCase() : "";
        const role = user.role ? user.role.toLowerCase() : "";
        const projectName = user.projectName ? user.projectName.toLowerCase() : "";
        const mobileNo = user.mobileNo ? user.mobileNo.toLowerCase() : "";
        const proposalName = user.proposalName ? user.proposalName.toLowerCase() : ""; // Include proposal name in search

        return (
          username.includes(lowerSearchTerm) ||
          role.includes(lowerSearchTerm) ||
          projectName.includes(lowerSearchTerm) ||
          mobileNo.includes(lowerSearchTerm) ||
          proposalName.includes(lowerSearchTerm)
        );
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredUsers(result);
  }, [users, searchTerm, sortConfig]);

  // Reset the form to its initial empty state
  const resetForm = () => {
    setFormData({
      username: "",
      usr_id: null,
      mobileNo: "",
      role: "",
      user_role_id: null,
      projectName: "",
      project_id: null,
      proposalName: "",
      acquisition_proposal_id: null,
      status: false,
    });
    setFormErrors({
      username: "",
      mobileNo: "",
      role: "",
      projectName: "",
      proposalName: "",
    });
    setTouchedFields({});
    setEditingUserId(null);
    setUsernameSearchTerm("");
    setSearchResults([]);
    setShowSearchResults(false);
    setProposals([]); // Clear proposals when form is reset
    setAlertMessage({ type: "", message: "" }); // Clear alert when form is reset
  };

  // Handle toggling user status (active/inactive)
  const handleToggle = async (id) => {
    if (!id) return;

    const userToUpdate = users.find((user) => user.id === id);
    if (!userToUpdate) return;

    // Find the role ID from the roles list based on the user's role name
    const selectedRole = roles.find((r) => r.name === userToUpdate.role);

    try {
      // Create the update payload for addAssignRole API
      const updateData = {
        usr_id: userToUpdate.usr_id,
        usr_role: selectedRole ? selectedRole.id : null, // Send role ID
        project_id: userToUpdate.project_id || null,
        acquisition_proposal_id: userToUpdate.acquisition_proposal_id || null, // Use consistent naming
        is_active: userToUpdate.status ? 0 : 1, // Toggle status: if active (true) send 0 (inactive), if inactive (false) send 1 (active)
      };

      await apiService.addAssignRole(updateData);

      // Update local state after successful API call
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, status: !user.status } : user
        )
      );
      toast.success(
        `User status ${!userToUpdate.status ? "activated" : "deactivated"} successfully`
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status. Please try again.");
    }
  };

  // Handle editing an existing user role
  const handleEdit = (user) => {
    if (!user) return;

    // Find full project and proposal objects to get their IDs
    const selectedProject = projects.find((p) => p.name === user.projectName);
    const selectedProposal = proposals.find((pr) => pr.name === user.proposalName); // Corrected to proposalName
    const selectedRole = roles.find((r) => r.name === user.role);

    setFormData({
      username: user.username,
      usr_id: user.usr_id,
      mobileNo: user.mobileNo,
      role: user.role, // Display name
      user_role_id: selectedRole ? selectedRole.id : null, // ID for API
      projectName: user.projectName || "", // Display name
      project_id: selectedProject ? selectedProject.id : null, // ID for API
      proposalName: user.proposalName || "", // Display name
      acquisition_proposal_id: selectedProposal ? selectedProposal.id : null, // ID for API
      status: user.status,
    });
    setFormErrors({
      username: "",
      mobileNo: "",
      role: "",
      projectName: "",
      proposalName: "",
    });
    setTouchedFields({});
    setEditingUserId(user.id);
    setUsernameSearchTerm(user.username); // Set the username search term to show the current username in the search field
    setModalOpen(true);
    // Fetch proposals for the selected project if applicable
    if (selectedProject) {
      fetchProposals(selectedProject.id);
    }
    setAlertMessage({ type: "", message: "" }); // Clear alert when editing
  };

  // Handle adding a new user role
  const handleAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  // Handle user selection from search results dropdown
  const handleUserSelect = (user) => {
    setFormData((prev) => ({
      ...prev,
      username: user.username,
      usr_id: user.id,
      mobileNo: user.mobileNo,
    }));
    setShowSearchResults(false);
    setUsernameSearchTerm(user.username); // Keep the selected username in the search input
  };

  // Validate form fields before submission
  const validateForm = () => {
    let isValid = true;
    const errors = {
      username: "",
      mobileNo: "",
      role: "",
      projectName: "",
      proposalName: "",
    };

    if (!formData.username.trim() || formData.usr_id === null) {
      errors.username = "Please select a user from the search results.";
      isValid = false;
    }

    if (!formData.role || formData.user_role_id === null) {
      errors.role = "Please select a role.";
      isValid = false;
    }

    // Validate project name and proposal name based on role
    if (!rolesWithoutProposal.includes(formData.role)) {
      if (!formData.project_id) {
        errors.projectName = "Project name is required for this role.";
        isValid = false;
      }
      if (!formData.acquisition_proposal_id) {
        errors.proposalName = "Proposal name is required for this role.";
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission (add or update user role)
  const handleSubmit = async () => {
    if (!validateForm()) {
      setAlertMessage({
        type: "error",
        message: "Please correct the form errors.",
      });
      return;
    }

    setSubmitLoading(true); // Set loading true on submission
    setAlertMessage({ type: "", message: "" }); // Clear previous alerts

    try {
      // Prepare the request payload for addAssignRole API
      const roleData = {
        usr_id: formData.usr_id,
        usr_role: formData.user_role_id, // Send role ID
        project_id: !rolesWithoutProposal.includes(formData.role)
          ? formData.project_id
          : null,
        acquisition_proposal_id: !rolesWithoutProposal.includes(formData.role)
          ? formData.acquisition_proposal_id
          : null, // Use consistent naming
        is_active: formData.status ? 1 : 0, // Convert boolean status to 1/0
      };

      const response = await apiService.addAssignRole(roleData);

      if (response && response.status === 200) {
        alert(`User role ${editingUserId !== null ? "updated" : "assigned"} successfully!`);
        // setAlertMessage({
        //   type: "success",
        //   message: `User role ${editingUserId !== null ? "updated" : "assigned"} successfully!`,
        // });
        setModalOpen(false);
        resetForm();
        fetchAllData(); // Auto-refresh table data
      } else {
        setAlertMessage({
          type: "error",
          message: "Failed to save user data. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting user data:", error);
      setAlertMessage({
        type: "error",
        message: "Failed to save user data. Please try again.",
      });
    } finally {
      setSubmitLoading(false); // Always reset loading state
    }
  };

  // Handle sorting table columns
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator for table headers
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "ascending" ? " ↑" : " ↓";
  };

  // Refresh all data
  const refreshData = () => {
    setLoading(true);
    fetchAllData();
  };

  // Function to open download modal
  const handleDownload = () => {
    setDownloadModalOpen(true);
  };

  // Define columns for download
  const getColumnsForDownload = () => {
    return [
      { header: "S.No.", value: "index" },
      { header: "Username", value: "username" },
      { header: "Mobile No", value: "mobileNo" },
      { header: "Role", value: "role" },
      { header: "Project Name", value: "projectName" },
      { header: "Proposal Name", value: "proposalName" }, // Added proposal name to download
      // { header: "Status", value: "status" },
    ];
  };

  // Prepare data for download with formatted status
  const getDataForDownload = () => {
    return filteredUsers.map((user, index) => ({
      index: index + 1, // Add S.No.
      username: user.username,
      mobileNo: user.mobileNo,
      role: user.role,
      projectName: user.projectName || "N/A",
      proposalName: user.proposalName || "N/A", // Handle null/undefined
      status: user.status ? "Active" : "Inactive",
    }));
  };

  // Handle input changes in the form fields
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Special handling for role selection
    if (field === "role") {
      const selectedRole = roles.find((r) => r.id === parseInt(value));
      if (selectedRole) {
        setFormData((prev) => ({
          ...prev,
          role: selectedRole.name,
          user_role_id: selectedRole.id,
          // Reset project and proposal if role changes to one that doesn't require them
          ...(rolesWithoutProposal.includes(selectedRole.name) && {
            projectName: "",
            project_id: null,
            proposalName: "",
            acquisition_proposal_id: null,
          }),
        }));
        setProposals([]); // Clear proposals when role changes
      }
    }
    // Special handling for project selection
    if (field === "projectName") {
      const selectedProject = projects.find((p) => p.id === parseInt(value));
      if (selectedProject) {
        setFormData((prev) => ({
          ...prev,
          projectName: selectedProject.name,
          project_id: selectedProject.id,
          acquisition_proposal_id: null, // Reset proposal when project changes
          proposalName: "", // Reset proposal name display
        }));
        fetchProposals(selectedProject.id); // Fetch proposals for the newly selected project
      } else {
        // If no project is selected (e.g., user clears selection)
        setFormData((prev) => ({
          ...prev,
          projectName: "",
          project_id: null,
          acquisition_proposal_id: null,
          proposalName: "",
        }));
        setProposals([]); // Clear proposals
      }
    }

    // Special handling for proposal selection
    if (field === "proposalName") {
      const selectedProposal = proposals.find((pr) => pr.id === parseInt(value));
      if (selectedProposal) {
        setFormData((prev) => ({
          ...prev,
          proposalName: selectedProposal.name,
          acquisition_proposal_id: selectedProposal.id,
        }));
      } else {
        // If no proposal is selected
        setFormData((prev) => ({
          ...prev,
          proposalName: "",
          acquisition_proposal_id: null,
        }));
      }
    }

    // Mark the field as touched
    setTouchedFields((prev) => ({ ...prev, [field]: true }));

    // Clear the error when the user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // For username search input
    if (field === "usernameSearch") {
      setUsernameSearchTerm(value);
      if (value.length >= 3) {
        searchUsers(value);
      } else {
        setShowSearchResults(false);
        setSearchResults([]); // Clear search results if search term is too short
      }
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen font-inter">
      {/* Global Alert Message (visible if set) */}
      {alertMessage.message && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-md shadow-lg z-50 flex items-center ${alertMessage.type === "success"
            ? "bg-green-500 text-white"
            : alertMessage.type === "error"
              ? "bg-red-500 text-white"
              : "bg-blue-500 text-white"
            }`}
          role="alert"
        >
          {alertMessage.type === "success" ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : alertMessage.type === "error" ? (
            <XCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2" />
          )}
          <span>{alertMessage.message}</span>
          <button
            onClick={() => setAlertMessage({ type: "", message: "" })}
            className="ml-auto focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
        <div className="p-6">
          {/* Search and filters */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder={translations[language].searchuserproject}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={refreshData}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />{" "}
                {translations[language].refresh}
              </button>

              {/* Add download button */}
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                disabled={filteredUsers.length === 0}
              >
                <Download className="mr-1 h-4 w-4" /> {translations[language].export}
              </button>

              <button
                onClick={handleAdd}
                className="px-5 py-3 text-sm bg-gradient-to-r from-blue-600 to-gray-900 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-blue-200 hover:shadow-blue-300 group"
              >
                <Plus className="mr-1 w-4 h-4" /> {translations[language].addUser}
              </button>
            </div>
          </div>

          {/* Users table */}
          <div className="w-full overflow-visible rounded-lg border border-gray-200 shadow-sm">
            <table className="w-full table-auto divide-y divide-gray-200">

              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort("username")}
                  >
                    <span className="flex items-center">
                      {translations[language].username}
                      {getSortIndicator("username")}
                    </span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort("mobileNo")}
                  >
                    <span className="flex items-center">
                      {translations[language].mobileNo}
                      {getSortIndicator("mobileNo")}
                    </span>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort("role")}
                  >
                    <span className="flex items-center">
                      {translations[language].role}
                      {getSortIndicator("role")}
                    </span>
                  </th>
                  {/* <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort("status")}
                  >
                    <span className="flex items-center">
                      {translations[language].status}
                      {getSortIndicator("status")}
                    </span>
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations[language].actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-16">
                      <div className="flex justify-center items-center h-80">
                        <div className="text-center">
                          <div className="inline-flex relative mb-4">
                            <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                            <div
                              className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0"
                              style={{
                                animationDuration: "1.5s",
                                animationDirection: "reverse",
                              }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <User className="h-6 w-6 text-purple-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="text-gray-400 text-lg font-medium">
                            {translations[language].loadingusers || "Loading users"}...
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            {translations[language].Please_wait_while_we_fetch_the_user_data}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-normal max-w-xs overflow-auto">

                        <div className="font-medium text-gray-900 px-6 py-3 min-w-[120px]">{user.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-normal max-w-xs overflow-auto">

                        <div className="font-medium text-gray-900 px-6 py-3 min-w-[120px]">{user.mobileNo}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-normal max-w-xs overflow-auto">

                        <div className="flex flex-col">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === "Project Manager / Coordinator"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-indigo-100 text-indigo-800"
                              }`}
                          >
                            {user.role}
                          </span>

                          {/* Show project name inline for roles that have one */}
                          {!rolesWithoutProposal.includes(user.role) &&
                            user.projectName && (
                              <div className="flex flex-col items-start mt-1 ml-1 text-xs text-gray-600">
                                {user.projectName.split(';').map((name, idx) => (
                                  <div key={idx} className="flex items-center mb-0.5">
                                    <Briefcase className="h-3 w-3 text-purple-600 mr-1" />
                                    <span>{name.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                          {/* Show proposal name inline for roles that have one */}
                          {!rolesWithoutProposal.includes(user.role) &&
                            user.proposalName && (
                              <div className="flex flex-col items-start mt-1 ml-1 text-xs text-gray-600">
                                {user.proposalName.split(';').map((name, idx) => (
                                  <div key={idx} className="flex items-center mb-0.5">
                                    <Briefcase className="h-3 w-3 text-blue-600 mr-1" />
                                    <span>{name.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                        </div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-normal max-w-xs overflow-auto">

                        <div className="flex items-center">
                          <div
                            onClick={() => handleToggle(user.id)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${user.status ? "bg-blue-600" : "bg-gray-300"
                              }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${user.status ? "translate-x-6" : "translate-x-1"
                                }`}
                            />
                          </div>
                          <span
                            className={`ml-2 text-sm font-medium ${user.status ? "text-blue-600" : "text-gray-500"
                              }`}
                          >
                            {user.status
                              ? translations[language].active
                              : translations[language].inactive}
                          </span>
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEdit(user)}
                            className="flex px-4 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 flex items-center space-x-2 group"
                          >
                            <Edit size={20} className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                            <span>{translations[language].Edit}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        {searchTerm ? (
                          <>
                            <p className="text-lg font-medium">
                              {translations[language].Nousersfound}
                            </p>
                            <p className="text-sm">{translations[language].Tryfilter}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-medium">
                              {translations[language].noUsers}
                            </p>
                            <p className="text-sm">{translations[language].addUserPrompt}</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* User count summary */}
          <div className="mt-4 text-sm text-gray-600">
            {!loading && (
              <p>
                {translations[language].showing} {filteredUsers.length} {translations[language].of} {users.length} {translations[language].users}
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* User Form Modal */}
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
              {editingUserId !== null ? (
                <>
                  <Edit className="mr-2 text-indigo-600" size={20} />
                  {translations[language].edituserrole}
                </>
              ) : (
                <>
                  <Plus className="mr-2 text-blue-600" size={20} />
                  {translations[language].title}
                </>
              )}
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations[language].searchUsername}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={usernameSearchTerm}
                    onChange={(e) => handleInputChange("usernameSearch", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.username ? "border-red-500" : "border-gray-300"
                      }`}
                    placeholder={translations[language].SearchUsername}
                  />
                  {searchLoading && (
                    <div className="absolute right-3 top-2">
                      <svg
                        className="animate-spin h-5 w-5 text-indigo-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Search results dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        className="p-2 hover:bg-gray-100 cursor-pointer flex flex-col"
                        onClick={() => handleUserSelect(user)}
                      >
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="font-medium">{user.username}</span>
                        </div>
                        <div className="ml-6 text-xs text-gray-500 flex flex-col">
                          <span>{user.mobileNo}</span>
                          {user.email && <span>{user.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {touchedFields.username && formErrors.username && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations[language].role}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.user_role_id || ""}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.role ? "border-red-500" : "border-gray-300"
                    } bg-white`}
                  style={{ backgroundImage: "none" }}
                >
                  <option value="" className="text-gray-500">
                    {translations[language].Select_Role}
                  </option>
                  {roles.map((role) => (
                    <option
                      key={role.id}
                      value={role.id}
                      className="bg-green-50 text-gray-800 py-2 hover:bg-green-100"
                      style={{ backgroundColor: '#f0fdf4', color: '#1f2937' }}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
                {touchedFields.role && formErrors.role && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.role}</p>
                )}
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Designation <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.user_role_id || ""}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.role ? "border-red-500" : "border-gray-300"} bg-white`}
                  style={{ backgroundImage: "none" }}
                >
                  <option value="" className="text-gray-500">Select Designation</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.designation} ({role.designation})
                    </option>
                  ))}
                </select>
                {touchedFields.role && formErrors.role && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.role}</p>
                )}
              </div> */}

              {/* Display selected project names and proposal names ONLY during edit mode */}
              {editingUserId !== null && formData.projectName && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language].Assigned_Projects_and_Proposals}:
                  </label>
                  {formData.projectName.split(';').map((name, idx) => (
                    <div key={`project-${idx}`} className="flex items-center mb-1">
                      <Briefcase className="h-4 w-4 text-purple-600 mr-2" />
                      <span className="text-sm text-green-700">{translations[language].Project}: {name.trim()}</span>
                    </div>
                  ))}
                  {formData.proposalName && formData.proposalName.split(';').map((name, idx) => (
                    <div key={`proposal-${idx}`} className="flex items-center mb-1">
                      <Briefcase className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm text-green-700">{translations[language].Proposal}: {name.trim()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Show project selection if role is NOT in rolesWithoutProposal */}
              {formData.role && !rolesWithoutProposal.includes(formData.role) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations[language].PROJECT}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.project_id || ""}
                    onChange={(e) => handleInputChange("projectName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.projectName ? "border-red-500" : "border-gray-300"
                      } bg-white`}
                    style={{ backgroundImage: "none" }}
                  >
                    <option value="" className="text-gray-500">
                      {translations[language].selectproject}
                    </option>
                    {projects.map((project) => (
                      <option
                        key={project.id}
                        value={project.id}
                        className="bg-green-50 text-gray-800 py-2 hover:bg-green-100"
                        style={{ backgroundColor: '#f0fdf4', color: '#1f2937' }}
                      >
                        {project.name}
                      </option>
                    ))}
                  </select>
                  {touchedFields.projectName && formErrors.projectName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.projectName}</p>
                  )}
                </div>
              )}

              {/* Show proposal selection if role is NOT in rolesWithoutProposal and a project is selected */}
              {formData.role &&
                !rolesWithoutProposal.includes(formData.role) &&
                formData.project_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {translations[language].PROPOSAL}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.acquisition_proposal_id || ""}
                      onChange={(e) =>
                        handleInputChange("proposalName", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.proposalName ? "border-red-500" : "border-gray-300"
                        } bg-white`}
                      style={{ backgroundImage: "none" }}
                    >
                      <option value="" className="text-gray-500">
                        {translations[language].selectproposal}
                      </option>
                      {proposals.map((proposal) => (
                        <option
                          key={proposal.id}
                          value={proposal.id}
                          className="bg-green-50 text-gray-800 py-2 hover:bg-green-100"
                          style={{ backgroundColor: '#f0fdf4', color: '#1f2937' }}
                        >
                          {proposal.name}
                        </option>
                      ))}
                    </select>
                    {touchedFields.proposalName && formErrors.proposalName && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.proposalName}</p>
                    )}
                  </div>
                )}

              {/* <div className="flex items-center space-x-2 mt-2">
                <span className="text-sm font-medium text-gray-700">
                  {translations[language].Status}:
                </span>
                <div
                  onClick={() => setFormData((prev) => ({ ...prev, status: !prev.status }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${formData.status ? "bg-blue-500" : "bg-gray-300"
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${formData.status ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </div>
                <span
                  className={`text-sm font-medium ${formData.status ? "text-green-600" : "text-gray-500"
                    }`}
                >
                  {formData.status
                    ? translations[language].active
                    : translations[language].inactive}
                </span>
              </div> */}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={submitLoading}
              >
                {translations[language].Cancel}
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium rounded-lg shadow transition-all"
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {translations[language].saving}
                  </span>
                ) : editingUserId !== null ? (
                  translations[language].update
                ) : (
                  translations[language].save
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      <DownloadModal
        isOpen={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        title={translations[language].RoleManagement}
        data={getDataForDownload()}
        columns={getColumnsForDownload()}
        isLoading={downloadLoading}
        setIsLoading={setDownloadLoading}
        screenName="Role Management"
      />
    </div>
  );
};

export default RoleManagement;